// Cómo se NOMBRA la conversión de un cliente y la de cada una de sus cuentas. Helpers puros: sin
// JSX y sin estado, el texto sale por el `t` que recibe cada uno (mismo criterio que
// `panelFormatters`).
//
// Viven acá y no dentro de `ConversionModal` —que es de donde salieron— porque los usan tres
// superficies del panel: el chip del header, las filas de las fuentes y el propio modal. Importar
// una función de texto desde el modal arrastraba `EditEventsModal` y con él media app v1
// (`MultiSelect`, `APIClient`, reactstrap): en tests eso hacía caer suites enteras por un helper de
// cuatro líneas, y en producción metía todo ese árbol en el bundle de quien sólo quería el label.

import {CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_PAID_MEDIA, FACEBOOK_CHANNEL} from '../../channels/channels';
import {getTranslatedName, PREFIX_CUSTOM_ACTION, stripActionPrefix} from '../../selects/actionCategories';

export const MODE_SPECIFIC = 'SPECIFIC';
export const MODE_ALL_PRIMARY = 'ALL_PRIMARY';
export const MODE_MIXED = 'MIXED';

// Cuántos eventos se nombran en la fila de una cuenta antes de resumir. Con más, la fila se come el
// modal y ninguno de los nombres se termina de leer.
const MAX_ACTION_NAMES = 3;

// GOTCHA: Meta se representa como 'FACEBOOK' en todo el FE (`channels.js`). Un 'META' crudo cae por
// todos los `if` de ChannelLogo y no renderiza NADA, sin error.
export function normalizeChannel(channel) {
    return channel === 'META' ? FACEBOOK_CHANNEL : channel;
}

export function channelInfo(account) {
    return CHANNEL_INFO_BY_CHANNEL[normalizeChannel(account && account.channel)] || null;
}

/**
 * Qué conversión monitorea el cliente, en una línea (`panel.monitored_conversion`).
 *
 * Es el MISMO texto en el chip del header y adentro del modal a propósito: si el chip dijera
 * "Compras" y el modal "Personalizada por cuenta", el usuario no sabría cuál creer.
 *
 * `null` (sin cuentas paid) ⇒ devuelve `null` y el chip no se pinta.
 */
export function monitoredConversionLabel(conversion, t) {
    const mode = conversion && conversion.mode;
    if (!mode) return null;
    if (mode === MODE_SPECIFIC) return conversion.label || null;
    if (mode === MODE_ALL_PRIMARY) {
        return t('client_panel.conversion_all_primary', 'Todas las conversiones primarias');
    }
    if (mode === MODE_MIXED) return t('client_panel.conversion_mixed', 'Personalizada por cuenta');
    return null;
}

/**
 * Las cuentas sobre las que se define la conversión: SOLO paid media.
 *
 * GA4 y las tiendas también aportan conversiones al análisis, pero no son las que la agencia
 * "monitorea" en el sentido del panel (el gasto y las alertas viven en paid). GA4 viaja en el mismo
 * `client_ad_accounts` con `channel: 'ANALYTICS'`, así que el filtro va por `channelType`.
 */
export function paidAdAccounts(accounts) {
    return (accounts || []).filter((account) => {
        const info = channelInfo(account);
        return !!info && info.channelType === CHANNEL_TYPE_PAID_MEDIA;
    });
}

/**
 * ¿Esta cuenta no está contando NADA?
 *
 * Es el estado que el panel rotula "Sin configurar" y el que deja las métricas de conversión en
 * cero sin que nada más lo explique. Se pregunta aparte del label porque el label es texto y esto
 * decide si la fila ofrece una salida.
 */
export function accountHasNoEvents(account) {
    const info = channelInfo(account) || {};
    if (!info.hasActions) return false;
    if (info.hasPrimaryConversions && account.use_all_primary_conversions) return false;
    return (account.selected_actions || []).length === 0;
}

/**
 * Las cuentas de paid media que no están contando NADA.
 *
 * El chip del encabezado resume la conversión del cliente y se calcula IGNORANDO este caso: con
 * dos cuentas en "todas las primarias" y una tercera sin configurar, el chip dice "Todas las
 * conversiones primarias" y no hay nada en el panel que delate que una cuenta mide cero. El
 * usuario se entera recién si abre el modal — o si algún día mira el número y no cierra.
 */
export function unconfiguredPaidAccounts(accounts) {
    return paidAdAccounts(accounts).filter(accountHasNoEvents);
}

/**
 * Los eventos elegidos de una cuenta cuyo nombre NO se puede resolver en el navegador.
 *
 * El diccionario de `actionCategories` sólo conoce los eventos estándar de Meta. Las conversiones
 * de Google (y las personalizadas de Meta) viajan como `CUSTOM_ACTION_<id numérico>`, y su nombre
 * vive sólo en el catálogo de la cuenta (`agency/ad-accounts/<id>/actions`): sin pedirlo, lo único
 * que hay para mostrar es el id.
 */
export function unnamedSelectedActions(account) {
    return (account.selected_actions || []).filter((action) => (
        typeof action === 'string' && action.startsWith(PREFIX_CUSTOM_ACTION) && !getTranslatedName(action)));
}

/**
 * ¿El label de esta cuenta, tal como está configurada, lee los eventos elegidos?
 *
 * Es la misma decisión que toman las dos primeras guardas de `accountConversionLabel`, en un solo
 * lugar: una cuenta en «todas las primarias» puede arrastrar `selected_actions` viejos que el label
 * nunca muestra, y pedir su catálogo por ellos eran 3 queries (y hasta la API de la plataforma) a
 * cambio de nada.
 */
function labelReadsSelectedActions(account) {
    const info = channelInfo(account) || {};
    if (!info.hasActions) return false;
    return !(info.hasPrimaryConversions && account.use_all_primary_conversions);
}

/** ¿Hace falta el catálogo de la cuenta para nombrar lo que el label va a mostrar? */
export function accountNeedsCatalog(account) {
    return labelReadsSelectedActions(account) && unnamedSelectedActions(account).length > 0;
}

/**
 * Cómo está configurada HOY una cuenta: todas las primarias, los eventos elegidos, o nada.
 *
 * `namesById` ({action_id: nombre}) es el catálogo de ESA cuenta, cuando el que llama lo tiene. Sin
 * él, las conversiones personalizadas se resumen en genérico y CONTADAS («3 conversiones
 * personalizadas»): un id crudo se lee como el nombre de la conversión (card zhvaJgpF), y un
 * genérico sin número esconde cuántas eligió el usuario.
 */
export function accountConversionLabel(account, t, namesById = {}) {
    const info = channelInfo(account) || {};
    if (!info.hasActions) {
        return t('client_panel.conversion_no_events', 'Este canal no maneja eventos de conversión');
    }
    if (!labelReadsSelectedActions(account)) {
        return t('client_panel.conversion_all_primary', 'Todas las conversiones primarias');
    }

    const unnamed = new Set(unnamedSelectedActions(account));
    const names = [];
    let unresolved = 0;
    (account.selected_actions || []).forEach((action) => {
        if (!unnamed.has(action)) names.push(getTranslatedName(action) || stripActionPrefix(action));
        else if (namesById[action]) names.push(namesById[action]);
        else unresolved += 1;
    });
    if (unresolved > 0) {
        names.push(t('client_panel.conversion_custom_unnamed', {
            count: unresolved,
            defaultValue_one: 'Conversión personalizada',
            defaultValue_other: '{{count}} conversiones personalizadas',
        }));
    }
    if (!names.length) return t('client_panel.conversion_account_none', 'Sin configurar');
    if (names.length <= MAX_ACTION_NAMES) return names.join(' · ');
    return `${names.slice(0, MAX_ACTION_NAMES).join(' · ')} +${names.length - MAX_ACTION_NAMES}`;
}
